<script lang="ts" setup>import { computed as _computed, ref as _ref } from 'vue';

import { useElementSize, watchDebounced } from '@vueuse/core'
import GiftRecommendationCard from '@consumer/modules/myAccount/shared/recommendations/GiftRecommendationCard.vue'
import { giftRecommendations, locations as locationsApi } from '@consumer/api'
import { dedupeGifts, giftDedupeKey } from '@consumer/utils/giftDedupe'
import { useGiftBadging, type RecommendedGift } from '@consumer/composables/giftBadging'
import { signalFilled, useConciergeState, type SignalValues } from '@consumer/modules/buyersConcierge/conciergeState'
import { hasUnplacedContent, matchSignals } from '@consumer/modules/buyersConcierge/parseSignals'
import { recommendationReadiness } from '@consumer/modules/buyersConcierge/recommendationRequest'
import {
  fetchKey,
  recoveredToApply,
  shouldFetch,
  shouldRefine,
} from '@consumer/modules/buyersConcierge/searchTriggers'
import { trackConciergeEvent, conciergeQueryMetadata } from '@consumer/modules/buyersConcierge/conciergeEventTracking'
import { useConciergeTakeover } from '@consumer/modules/buyersConcierge/useConciergeTakeover'
import type { ConciergeInputVariantName } from '@consumer/modules/buyersConcierge/ConciergeInputVariant.vue'

// Results for the Buyer's Concierge, rendered directly by the concierge takeover: one
// rank-ordered grid — the combined feed (no `section`), so local gifts lead, then the
// occasion-led nationwide pool, then the generic fallback — paged by "See more". Driven
// by the concierge store's signals rather than a saved subject; the detected-signal
// chips above (in ConciergeInput) already summarize the basis.
type Gift = RecommendedGift

withDefaults(defineProps<{ variant?: ConciergeInputVariantName }>(), { variant: 'free_text', })

const state = useConciergeState()

// Rendered without a takeover (Storybook), the grid is never capped.
const takeover = useConciergeTakeover()
const engaged = _computed(() => takeover?.engaged.value ?? true)

const PAGE_SIZE = 18
// Counted from the input's own 220ms parse pause, so a search starts ~1.2s after the last
// keystroke: long enough that a mid-sentence pause doesn't fill the canvas with gifts for
// the half-typed prompt.
const FETCH_DEBOUNCE_MS = 1000
// Two rows at the widest grid, so the first paint fills the canvas rather than one row.
const SKELETON_COUNT = 6
const LOAD_MORE_SKELETON_COUNT = 3
// At rest — inline on the homepage after a dismiss, or on a return visit with persisted
// signals — the grid shows one row; "See all" reopens the takeover. Mirrors the grid's own
// container breakpoint below, so the row is whole at every width.
const THREE_COLUMNS_MIN_WIDTH = 640

// The signal slots the AI refine can fill; budget is excluded (we never nudge a price).
const REFINABLE_SIGNALS = ['relationship', 'occasion', 'location', 'interests'] as const

const readiness = _computed(() => recommendationReadiness(state.signals))

// The engine's locality ranking keys off a canonical "City, ST" (its saved-subject
// callers get that from the location picker); the concierge parses free text to a
// bare city, so resolve it through the same featured-city autocomplete before it
// drives the query — otherwise local gifts never lead. Already-canonical input
// ("Seattle, WA") passes through untouched.
let resolvedLocation = _ref<string | null>(null)

// Seq guard: the search is async, so a slower earlier city must not overwrite a
// newer one (or a since-cleared field). Bumped on every call so an in-flight
// search whose input is stale drops its result.
let resolveSeq = 0

async function resolveLocation (city: string | null) {
  const seq = ++resolveSeq
  if (!city) {
    resolvedLocation.value = null
    return
  }
  // Only skip resolution for the exact canonical "City, ST" the backend matches
  // on (comma-space + uppercase state); anything looser is normalized by the search.
  if (/, [A-Z]{2}$/.test(city)) {
    resolvedLocation.value = city
    return
  }
  try {
    const matches = await locationsApi.search<{ value: string }[]>({ data: { term: city } })
    if (seq !== resolveSeq) return
    resolvedLocation.value = matches?.[0]?.value ?? null
  }
  catch {
    if (seq === resolveSeq) resolvedLocation.value = null
  }
}

watch(() => state.signals.location.value, resolveLocation, { immediate: true })

// snake_case keys: sent straight through as query params to the concierge source.
const conciergeQuery = _computed(() => ({
  recommendation_source: 'concierge',
  relationship: state.signals.relationship.value ?? undefined,
  occasion: state.signals.occasion.value ?? undefined,
  interests: state.signals.interests.value,
  location: resolvedLocation.value ?? undefined,
}))

const interests = _computed(() => state.signals.interests.value)
const locations = _computed(() => (resolvedLocation.value ? [resolvedLocation.value] : []))

const { badgesFor, resolveLocationImages } = useGiftBadging({
  interests: (interests),
  locations: (locations),
})

let gifts = _ref<Gift[]>([])
let loading = _ref(false)
let loadingMore = _ref(false)
let hasMore = _ref(false)
let nextOffset = _ref(0)
// True once a fetch for the current query has settled: the empty state must not
// flash during the debounce before the first fetch fires.
let searched = _ref(false)

// Drops a stale/superseded response so a slower earlier fetch can't overwrite a
// newer one — and so an in-flight fetch can't land after the field is cleared.
// Mirrors ConciergeInput's parseSeq guard.
let fetchSeq = 0

let resultsRefinable = false

// The query a first page was requested with, so the results events describe the search that
// returned — a response can land during the debounce after the buyer has typed on.
let fetchedQueryMeta: Record<string, unknown> = {}

async function fetchGifts ({ append = false } = {}) {
  // No append while a replacement first page is in flight: it would supersede that fetch (whose
  // cleanup only runs when it is still the latest) and page the old query onto the new results.
  if (append && (loading.value || loadingMore.value || !hasMore.value)) return

  const seq = ++fetchSeq
  const requestedOffset = append ? nextOffset.value : 0
  if (!append) fetchedQueryMeta = conciergeQueryMetadata(__props.variant, state.raw, state.signals)
  if (append) loadingMore.value = true
  else loading.value = true

  try {
    const response = await giftRecommendations.index<{
      gifts: Gift[]
      hasMore?: boolean
      nextOffset?: number
      refinable?: boolean
    }>({
      query: {
        ...conciergeQuery.value,
        limit: PAGE_SIZE,
        offset: requestedOffset,
      },
    })
    if (seq !== fetchSeq) return

    const fetched = response.gifts ?? []
    gifts.value = append ? dedupeGifts([...gifts.value, ...fetched]) : fetched
    hasMore.value = response.hasMore ?? false
    // Advance by the server window (offset requested + items returned), not the
    // deduped client length — dedupe can shrink `gifts` below the server offset and
    // cause a page to be re-requested.
    nextOffset.value = response.nextOffset ?? (requestedOffset + fetched.length)
    if (!append) resultsRefinable = response.refinable ?? false
  }
  finally {
    // Always release `loadingMore` — only one append is ever in flight, so a
    // superseded one must still clear it or pagination stays blocked. `loading`
    // stays owned by the latest fetch, so only clear it when this call is latest.
    if (append) loadingMore.value = false
    else if (seq === fetchSeq) {
      loading.value = false
      searched.value = true
    }
  }
}

const root = useTemplateRef<HTMLElement>('root')
const { width: surfaceWidth } = useElementSize(root)
const atRestCap = _computed(() => (surfaceWidth.value >= THREE_COLUMNS_MIN_WIDTH ? 3 : 2))

const visibleGifts = _computed(() => (engaged.value ? gifts.value : gifts.value.slice(0, atRestCap.value)))
const showSeeAll = _computed(() => !engaged.value && (gifts.value.length > atRestCap.value || hasMore.value))
const seeAllLabel = _computed(() => `See all ${gifts.value.length}${hasMore.value ? '+' : ''} results`)

function cardLinks (): HTMLAnchorElement[] {
  return Array.from(root.value?.querySelectorAll<HTMLAnchorElement>('a[name="calendar-gift-recommendation"]') ?? [])
}

// Focus continues on the first appended card: the button that had it has moved below
// the new rows, and the takeover is a focus-trapped dialog.
async function loadMore () {
  if (loading.value || loadingMore.value || !hasMore.value) return
  trackConciergeEvent('results_more', { variant: __props.variant, metadata: { offset: nextOffset.value } })
  const firstAppended = gifts.value.length
  await fetchGifts({ append: true })
  await nextTick()
  cardLinks()[firstAppended]?.focus()
}

function seeAll () {
  takeover?.engage()
}

let lastRefinedText = ''

// AI-recover the signals the deterministic parser missed, then merge them into the
// store as `inferred` (never overriding what the buyer stated). The store change
// re-drives the normal fetch, so the cards and the detected chips update through one
// path — no bespoke swap.
async function refineSignals (raw: string) {
  lastRefinedText = raw
  try {
    const response = await giftRecommendations.refine<{ signals?: Partial<SignalValues> }>({
      data: { recommendation_source: 'concierge', raw },
    })
    // Clearing the marker lets the newer text refine on its next pause even when
    // the edit didn't add words.
    if (raw !== state.raw.trim()) {
      lastRefinedText = ''
      return
    }

    state.setRecovered(recoveredToApply(response.signals ?? {}))
  }
  catch {
    // Let the next pause retry; the deterministic results already stand.
    lastRefinedText = ''
  }
}

// The dedup key of the last fetch (see searchTriggers.fetchKey).
let fetchedQueryKey = ''

// A changed query is a new search: drop the last one's verdict so the debounce shows
// placeholders rather than a stale "no matches".
watch(() => [JSON.stringify(conciergeQuery.value), state.raw], () => { searched.value = false })

// Watches the raw text too, so the long-input fallback fires even when nothing parsed
// and `conciergeQuery` never changed. `immediate` covers a reload with a persisted brief
// whose input writes nothing new on mount (taps re-synthesizes the same sentence, so
// neither key moves); the dedupe below keeps free-text's mount re-parse, which lands on
// the same key, from fetching twice.
watchDebounced(
  () => [JSON.stringify(conciergeQuery.value), state.raw] as const,
  () => {
    if (!shouldFetch(state.raw, readiness.value.ready)) return

    const queryKey = fetchKey(readiness.value.ready, JSON.stringify(conciergeQuery.value), state.raw)
    if (queryKey === fetchedQueryKey) return
    fetchedQueryKey = queryKey
    resolveLocationImages()
    // Clear the key on failure so a transient error doesn't permanently suppress a
    // retry — and so the unawaited fetch can't reject unhandled.
    fetchGifts().catch(() => { fetchedQueryKey = '' })
  },
  { debounce: FETCH_DEBOUNCE_MS, immediate: true },
)

// Refine cadence is deliberately separate from — and much slower than — the fetch. An
// AI call fires only after a real typing pause AND once the buyer has added words since
// the last parse AND the current results still came back thin. That throttles the calls
// (no per-keystroke firing) and keeps re-checking as the buyer writes more, instead of
// going quiet after the first parse.
const REFINE_PAUSE_MS = 3000

watchDebounced(
  () => state.raw,
  () => {
    const inputs = {
      resultsRefinable,
      hasUnmatched: hasUnplacedContent(matchSignals(state.raw).unmatched),
      hasGap: REFINABLE_SIGNALS.some(name => !signalFilled(state.signals[name])),
      lastRefinedText,
    }
    if (!shouldRefine(state.raw, inputs)) return
    refineSignals(state.raw.trim())
  },
  { debounce: REFINE_PAUSE_MS },
)

// Emptying the field resets everything; short of that we keep the last results on
// screen while a new fetch runs, so cards never blink out mid-typing. Bump both seq
// guards so an in-flight fetch or city resolution can't land after the clear, and
// reset the paging flags so pagination isn't left stuck.
watch(() => state.raw, (raw) => {
  if (!raw.trim()) {
    fetchSeq++
    resolveSeq++
    fetchedQueryKey = ''
    lastRefinedText = ''
    resultsRefinable = false
    gifts.value = []
    loading.value = false
    loadingMore.value = false
    hasMore.value = false
    nextOffset.value = 0
    searched.value = false
    resolvedLocation.value = null
  }
})

// Per-card viewport impressions are deliberately not tracked.
watch(() => loading.value, (isLoading, wasLoading) => {
  if (!wasLoading || isLoading) return
  // Clearing/cancelling the field forces loading→false (and blanks fetchedQueryKey);
  // only emit results events for a real fetch completion, not a reset.
  if (!fetchedQueryKey) return
  const query = fetchedQueryMeta
  if (gifts.value.length > 0) {
    // `gifts` is capped by PAGE_SIZE; `has_more` is what says how deep the pool runs.
    const readyMeta = { gifts: gifts.value.length, locations: locations.value.length, has_more: hasMore.value, ...query }
    trackConciergeEvent('results_ready', { variant: __props.variant, metadata: readyMeta })
  }
  else {
    trackConciergeEvent('results_empty', { variant: __props.variant, metadata: query })
  }
})

// Delegated so it covers every card without touching the shared GiftRecommendationCard.
function onResultClick (event: MouseEvent) {
  const target = event.target as HTMLElement | null
  const anchor = target?.closest<HTMLAnchorElement>('a[name="calendar-gift-recommendation"]') ?? null
  if (!anchor) return

  trackConciergeEvent('result_click', {
    variant: __props.variant,
    metadata: { href: anchor.getAttribute('href'), position: cardLinks().indexOf(anchor) },
  })
}
</script>

<template>
  <div
    v-if="readiness.ready || loading || gifts.length > 0"
    ref="root"
    class="concierge-recommendations"
    :aria-busy="loading || loadingMore"
    @click="onResultClick"
  >
    <div v-if="!gifts.length && (loading || (readiness.ready && !searched))" class="results-grid" aria-hidden="true">
      <GiftRecommendationCard v-for="i in SKELETON_COUNT" :key="`skeleton-${i}`" loading/>
    </div>

    <TransitionGroup v-else-if="gifts.length" tag="div" name="card" class="results-grid">
      <GiftRecommendationCard
        v-for="gift in visibleGifts"
        :key="giftDedupeKey(gift)"
        :idea="gift"
        :badges="badgesFor(gift)"
      />
    </TransitionGroup>

    <p v-else-if="searched && !loading" class="empty">
      No matches yet — try another detail.
    </p>

    <div v-if="loadingMore" class="results-grid" aria-hidden="true">
      <GiftRecommendationCard v-for="i in LOAD_MORE_SKELETON_COUNT" :key="`more-${i}`" loading/>
    </div>

    <div v-else-if="showSeeAll" class="see-more">
      <GButton
        variant="secondary"
        size="medium"
        :label="seeAllLabel"
        name="concierge-see-all"
        @click="seeAll"
      />
    </div>

    <div v-else-if="engaged && hasMore && gifts.length" class="see-more">
      <GButton
        variant="secondary"
        size="medium"
        label="See more"
        name="concierge-see-more"
        @click="loadMore"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Columns follow the width of the column the surface sits in, not the viewport: the same
// grid renders in the 720px at-rest column and the engaged canvas (capped at 1240px by
// `.concierge-surface__inner`, which is why three is the most it grows to).
.concierge-recommendations {
  container-type: inline-size;
  display: grid;
  gap: 16px;
  margin-top: 16px;
  width: 100%;
}

.results-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  // Grid items default to min-width:auto and won't shrink under the card art's
  // intrinsic 360px.
  > * {
    min-width: 0;
  }
}

@container (min-width: 640px) {
  .results-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.see-more {
  display: flex;
  justify-content: center;
}

.empty {
  color: var(--color-text-tertiary);
  font-size: 14px;
  margin: 0;
}

// Only cards new to the list fade in: keyed by the dedupe key, a refetch that keeps a
// gift keeps its element, so nothing already on screen re-enters.
.card-enter-active {
  transition: opacity 0.3s ease-out;
}

.card-enter-from {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .card-enter-active {
    transition: none;
  }
}
</style>
